import type { MutableRefObject, ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AutoHideOnScroll,
    DirectoryGridResults,
    PageTitle,
    SidebarMenu,
    type SidebarMenuItem,
} from '@/components';
import { SidePanel } from '@/components/SidePanel';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type {
    DirectoryGridConfig,
    DirectoryCardHandlers,
    EditModalRenderProps,
    DeleteConfirmRenderProps,
} from './types';
import { StatusFilter } from './types';
import { DirectoryGridActionBar } from './DirectoryGridActionBar';
import { DirectorySidebarTags } from './DirectorySidebarTags';
import { useDirectoryGrid } from './useDirectoryGrid';

interface DirectoryGridProps<
    TItem extends { id: string },
    TPayload = Record<string, unknown>,
> {
    config: DirectoryGridConfig<TItem, TPayload>;
    renderCard: (item: TItem, handlers: DirectoryCardHandlers) => ReactNode;
    renderEditModal: (
        props: EditModalRenderProps<TItem, TPayload>,
    ) => ReactNode;
    renderDeleteConfirmation?: (
        props: DeleteConfirmRenderProps<TItem>,
    ) => ReactNode;
    skeletonComponent: ReactNode;
    children?: ReactNode;
    onRevalidateRef?: MutableRefObject<(() => Promise<void>) | null>;
}

export function DirectoryGrid<
    TItem extends { id: string },
    TPayload = Record<string, unknown>,
>({
    config,
    renderCard,
    renderEditModal,
    renderDeleteConfirmation,
    skeletonComponent,
    children,
    onRevalidateRef,
}: DirectoryGridProps<TItem, TPayload>) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const {
        items,
        statusFilter,
        setStatusFilter,
        searchDraft,
        setSearchDraft,
        setSearchQuery,
        sortOption,
        setSortOption,
        selectedTags,
        tagOptions,
        toggleTag,
        clearTags,
        selectedVehicleTypes,
        toggleVehicleType,
        clearVehicleTypes,
        statusTabs,
        hasMore,
        isLoadingMore,
        isInitialLoading,
        loadMore,
        scrollContainer,
        setScrollContainer,
        handleItemClick,
        handleSelectAll,
        requestDeleteSelected,
        confirmDeleteSelected,
        cancelDeleteSelected,
        bulkDeleteCount,
        totalCount,
        isItemSelected,
        selection,
        isEditModalOpen,
        modalMode,
        itemToEdit,
        isUpdating,
        openEditModal,
        closeEditModal,
        handleUpdate,
        markAsUnknown,
        deleteConfirmId,
        isDeleting,
        handleDeleteClick,
        handleDeleteConfirm,
        handleDeleteCancel,
        revalidateAll,
    } = useDirectoryGrid<TItem, TPayload>(config);

    useEffect(() => {
        if (onRevalidateRef) {
            onRevalidateRef.current = revalidateAll;
        }
    }, [onRevalidateRef, revalidateAll]);

    const itemToDelete = deleteConfirmId
        ? items.find((item) => item.id === deleteConfirmId)
        : undefined;

    const isBulkDelete = bulkDeleteCount > 0;
    const isDeleteConfirmOpen = !!deleteConfirmId || isBulkDelete;
    const deleteCount = isBulkDelete ? bulkDeleteCount : 1;

    const statusSidebarItems: SidebarMenuItem[] = statusTabs.map((tab) => ({
        id: tab.id,
        label: tab.label,
        count: tab.count,
        disabled: tab.disabled,
    }));

    const sidebar = (
        <div className="flex flex-col">
            <SidebarMenu
                items={statusSidebarItems}
                activeId={statusFilter}
                onItemClick={(tabId) => setStatusFilter(tabId as StatusFilter)}
            />
            {config.fetchTags && (
                <DirectorySidebarTags
                    title={t('browseBar.tags')}
                    tags={tagOptions}
                    selectedTags={selectedTags}
                    onTagToggle={toggleTag}
                    onClear={clearTags}
                />
            )}
        </div>
    );

    return (
        <div className="relative h-full min-h-0">
            <SidePanel side="left">{sidebar}</SidePanel>
            <div className="h-full flex flex-col relative">
                <AutoHideOnScroll container={scrollContainer}>
                    <DirectoryGridActionBar
                        isMultiSelectMode={selection.isMultiSelectMode}
                        selectedCount={selection.selectedCount}
                        totalCount={totalCount}
                        searchValue={searchDraft}
                        onSearchChange={setSearchDraft}
                        onSearchSubmit={setSearchQuery}
                        searchPlaceholder={config.searchPlaceholder}
                        onEnterMultiSelect={selection.enterMultiSelectMode}
                        onExitMultiSelect={selection.exitMultiSelectMode}
                        onSelectAll={handleSelectAll}
                        onDeleteSelected={
                            config.bulkDeleteItems
                                ? requestDeleteSelected
                                : undefined
                        }
                        sortOptions={config.sortOptions}
                        sortOption={sortOption}
                        onSortChange={setSortOption}
                        vehicleTypeOptions={config.vehicleTypeOptions}
                        selectedVehicleTypes={selectedVehicleTypes}
                        onVehicleTypeToggle={toggleVehicleType}
                        onClearVehicleTypes={clearVehicleTypes}
                        selectionEntityLabel={config.selectionEntityLabel}
                    />
                </AutoHideOnScroll>

                <div
                    ref={setScrollContainer}
                    className="flex-1 overflow-y-auto scrollbar-hide min-h-0 pb-20 px-4 panels:px-8"
                >
                    <div className="w-full max-w-content mx-auto pt-20">
                        <div className="pt-4 pb-8">
                            <PageTitle
                                text={config.title}
                                breadcrumbs={
                                    config.parentTitle
                                        ? [{ label: config.parentTitle }]
                                        : undefined
                                }
                            />
                        </div>
                        <DirectoryGridResults<TItem>
                            items={items}
                            isItemSelected={isItemSelected}
                            isMultiSelectMode={selection.isMultiSelectMode}
                            deletingItemId={
                                isDeleting ? (deleteConfirmId ?? null) : null
                            }
                            skeletonComponent={skeletonComponent}
                            skeletonCount={12}
                            messages={{
                                emptyMessage: config.messages.empty,
                                loadingMoreMessage: config.messages.loadingMore,
                                endMessage: config.messages.allLoaded,
                            }}
                            onLoadMore={loadMore}
                            isLoadingMore={isLoadingMore}
                            hasMore={hasMore}
                            isInitialLoading={isInitialLoading}
                            scrollContainer={scrollContainer}
                            renderCard={(item, options) => {
                                const isSelectable =
                                    config.canDeleteItem?.(item) ?? true;
                                const selectionDisabled =
                                    options.isMultiSelectMode && !isSelectable;
                                return renderCard(item, {
                                    onClick: selectionDisabled
                                        ? () => {}
                                        : handleItemClick,
                                    onEdit: (id: string) =>
                                        openEditModal(id, 'edit'),
                                    onMarkAsRecognized: (id: string) =>
                                        openEditModal(id, 'recognize'),
                                    onMarkAsUnknown: markAsUnknown,
                                    onDelete: config.bulkDeleteItems
                                        ? handleDeleteClick
                                        : undefined,
                                    onMerge: config.onMergeSingleItem
                                        ? () => config.onMergeSingleItem!(item)
                                        : undefined,
                                    checked: options.isMultiSelectMode
                                        ? options.isSelected
                                        : undefined,
                                    isDeleting: options.isDeleting,
                                    selectionDisabled,
                                });
                            }}
                        />
                    </div>
                </div>
                {renderEditModal({
                    isOpen: isEditModalOpen,
                    onClose: closeEditModal,
                    isSubmitting: isUpdating,
                    mode: modalMode,
                    item: itemToEdit,
                    onUpdate: handleUpdate,
                })}
                {renderDeleteConfirmation?.({
                    isOpen: isDeleteConfirmOpen,
                    item: itemToDelete,
                    count: deleteCount,
                    isDeleting,
                    onConfirm: isBulkDelete
                        ? confirmDeleteSelected
                        : handleDeleteConfirm,
                    onCancel: isBulkDelete
                        ? cancelDeleteSelected
                        : handleDeleteCancel,
                })}
                <div className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none bg-gradient-to-t from-grey-700 to-transparent" />
                {children}
            </div>
        </div>
    );
}
